import { memo, useId, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import InsightsDeltaBadge, {
  insightsDeltaPillVisible,
  insightsUniformRowsReserveDeltaLane,
} from "./InsightsDeltaBadge";
import InsightsSectionHeading from "./InsightsSectionHeading";
import {
  muscleBarPrimaryBackground,
  muscleBarSecondaryBackground,
} from "../utils/insightsProgramInsightsColors";
import { muscleBarStackLayout } from "../utils/insightsMuscleBarLayout";

/**
 * Relative bar vs program-wide max: stack width `(musclePrimary + muscleSecondary) / globalMax`.
 * Secondary (transparent) fills the stack; primary (solid) covers the left `musclePrimary / rowTotal` share.
 * Tail shows `--white-10` for unused track.
 */
function MuscleBarTrack({
  muscleId,
  musclePrimary,
  muscleSecondary,
  muscleBarGlobalMax,
}) {
  const { totalPct, primaryShareOfStackPct, rowTotal } = muscleBarStackLayout(
    musclePrimary,
    muscleSecondary,
    muscleBarGlobalMax
  );
  const primaryBg = muscleBarPrimaryBackground(muscleId);
  const secondaryBg = muscleBarSecondaryBackground(muscleId);

  return (
    <div className="ProgramInsights-muscleBarTrack">
      <div className="ProgramInsights-muscleBarTrack-shell">
        {totalPct > 0 ? (
          <div
            className="ProgramInsights-muscleBarTrack-stack"
            style={{ width: `${totalPct}%` }}
          >
            <div
              className="ProgramInsights-muscleBarLayer ProgramInsights-muscleBarLayer--secondary"
              style={{ backgroundColor: secondaryBg }}
            />
            <div
              className="ProgramInsights-muscleBarLayer ProgramInsights-muscleBarLayer--primary"
              style={{
                width: `${primaryShareOfStackPct}%`,
                backgroundColor: primaryBg,
              }}
            />
          </div>
        ) : null}
        <div
          className="ProgramInsights-muscleBarTrack-tail"
          aria-hidden="true"
        />
      </div>
      <span className="ProgramInsights-muscleBarTotal">
        {rowTotal.toFixed(1)}
      </span>
    </div>
  );
}

MuscleBarTrack.propTypes = {
  muscleId: PropTypes.string.isRequired,
  musclePrimary: PropTypes.number.isRequired,
  muscleSecondary: PropTypes.number.isRequired,
  muscleBarGlobalMax: PropTypes.number.isRequired,
};

/**
 * Delta column for a muscle row: nothing, badge, or fixed-width spacer when the lane is reserved.
 * @param {{ delta?: object }} row
 * @param {boolean} reserveDeltaLane
 */
function muscleRowDeltaCell(row, reserveDeltaLane) {
  if (!reserveDeltaLane) {
    return null;
  }
  const showDeltaPill =
    insightsDeltaPillVisible(row.delta) || row.delta?.tone === "neutral";
  if (showDeltaPill) {
    return (
      <InsightsDeltaBadge delta={row.delta.value} tone={row.delta.tone} />
    );
  }
  return (
    <span
      className="ProgramInsights-metricRow-deltaCell ProgramInsights-muscleRow-deltaSpacer"
      aria-hidden="true"
    />
  );
}

/**
 * SETS BY MUSCLE GROUPS — legend + one row per muscle + week-over-week set deltas.
 *
 * @param {object} props
 * @param {Array<{ id: string, primary: number, secondary: number, delta?: object }>} props.muscles
 * @param {number} props.muscleBarGlobalMax — max `(primary+secondary)` across all weeks and muscles (bar scale).
 * @param {number} [props.syncRowIndex] — `data-insights-sync-row`; same index on this section in every week column for `useProgramInsightsCrossColumnRowSync` (see InsightsWeekColumn).
 */
function InsightsMuscleGroupsSection({
  muscles,
  muscleBarGlobalMax,
  syncRowIndex,
}) {
  const { t } = useTranslation("athlete-portal");
  const titleId = useId();

  const reserveDeltaLane = useMemo(
    () => insightsUniformRowsReserveDeltaLane(muscles),
    [muscles]
  );

  return (
    <section
      className="ProgramInsights-section"
      aria-labelledby={titleId}
      data-insights-sync-row={syncRowIndex}
    >
      <InsightsSectionHeading
        titleId={titleId}
        title={t("programs.programInsights.panel.sectionMuscleGroups")}
        infoAriaLabel={t(
          "programs.programInsights.panel.muscleGroupsSectionInfo"
        )}
      />
      <div className="ProgramInsights-muscleLegend">
        <span className="ProgramInsights-muscleLegend-item">
          <span className="ProgramInsights-muscleLegend-swatch ProgramInsights-muscleLegend-swatch--primary" />
          {t("programs.programInsights.panel.legendPrimary")}
        </span>
        <span className="ProgramInsights-muscleLegend-item">
          <span className="ProgramInsights-muscleLegend-swatch ProgramInsights-muscleLegend-swatch--secondary" />
          {t("programs.programInsights.panel.legendSecondary")}
        </span>
      </div>
      <div className="ProgramInsights-sectionBody ProgramInsights-sectionBody--muscle">
        {muscles.map((row) => (
          <div key={row.id} className="ProgramInsights-muscleRow">
            <div className="ProgramInsights-muscleName">
              {t(`programs.programInsights.panel.muscles.${row.id}`)}
            </div>
            <div className="ProgramInsights-muscleRow-main">
              <MuscleBarTrack
                muscleId={row.id}
                musclePrimary={row.primary}
                muscleSecondary={row.secondary}
                muscleBarGlobalMax={muscleBarGlobalMax}
              />
              {muscleRowDeltaCell(row, reserveDeltaLane)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

InsightsMuscleGroupsSection.propTypes = {
  muscles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      primary: PropTypes.number.isRequired,
      secondary: PropTypes.number.isRequired,
      delta: PropTypes.object,
    })
  ).isRequired,
  muscleBarGlobalMax: PropTypes.number.isRequired,
  syncRowIndex: PropTypes.number,
};

export default memo(InsightsMuscleGroupsSection);
